import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDownload, FiPrinter, FiBarChart2, FiFileText, FiCornerDownRight
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import useReportMemory from '../../hooks/useReportMemory';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { addPrintBrand } from '../../utils/printBranding';

const INVOICE_CODES = ['SI', 'NSI'];
const RECEIPT_CODES = ['CR', 'BR'];
const RETURN_CODES = ['SR', 'NSR'];
const SOURCE_CODES = ['QT', 'SO', 'DC'];
const SOURCE_LABEL = { QT: 'Quotation', SO: 'Sale Order', DC: 'Delivery Challan' };

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const fmt = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};
const fmtDate = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  return date.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SaleActivity = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore this report's filters + generated rows on Back
  // (0 extra Firebase reads). savedMem is null on a first visit → defaults apply.
  const [savedMem, saveMemory] = useReportMemory('sale-activity');
  const navigate = useNavigate();

  // Raw data
  const [invoices, setInvoices] = useState(() => savedMem?.invoices ?? []);
  const [receiptsByInvoice, setReceiptsByInvoice] = useState(() => savedMem?.receiptsByInvoice ?? {});
  const [returnsByInvoice, setReturnsByInvoice] = useState(() => savedMem?.returnsByInvoice ?? {});
  const [sourceById, setSourceById] = useState(() => savedMem?.sourceById ?? {});
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);
  const [selectedId, setSelectedId] = useState(null);

  // Filters
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? new Date().toISOString().split('T')[0]);
  const [customerFilter, setCustomerFilter] = useState(() => savedMem?.customerFilter ?? 'All');
  const [employeeFilter, setEmployeeFilter] = useState(() => savedMem?.employeeFilter ?? 'All');

  const loadDropdowns = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [fySnap, custSnap, empRows] = await Promise.all([
        getDocs(query(collection(db, 'companies', activeCompany.id, 'financialYears'), orderBy('startDate', 'desc'))),
        getDocs(query(collection(db, 'companies', activeCompany.id, 'customers'), orderBy('companyName'))),
        getEmployeesCached(activeCompany.id),
      ]);
      const fys = fySnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setEmployees([...empRows]);
      // Only seed the FY + date defaults on a fresh visit — a restored view keeps its saved filters.
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
          const fy = fys.find(f => f.id === activeFinancialYear.id);
          if (fy) setDateFrom(fy.startDate || '');
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
          setDateFrom(fys[0].startDate || '');
        }
      }
    } catch (e) { console.error('Error loading dropdowns:', e); }
  }, [activeCompany, activeFinancialYear, savedMem]);

  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);
  useEffect(() => {
    if (savedMem) return; // restored view: don't overwrite the saved Date From
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) setDateFrom(fy.startDate || '');
  }, [selectedFYId, financialYears, savedMem]);

  const inBranch = useCallback((t) => (!activeBranch || activeBranch.id === 'all') ? true : t.branchId === activeBranch.id, [activeBranch]);

  const handleGenerate = async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    setHasGenerated(true);
    setSelectedId(null);
    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const codes = [...INVOICE_CODES, ...RECEIPT_CODES, ...RETURN_CODES, ...SOURCE_CODES]; // 9 ≤ 10 in-limit
      const snap = await getDocs(query(transRef, where('tranTypeCode', 'in', codes), where('financialYearId', '==', selectedFYId)));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(inBranch);

      // Invoices (SI/NSI) with date + customer + employee filters
      let inv = all.filter(t => INVOICE_CODES.includes(t.tranTypeCode) && t.status !== 'Cancelled');
      if (dateFrom) inv = inv.filter(t => (t.docDate || '') >= dateFrom);
      if (dateTo) inv = inv.filter(t => (t.docDate || '') <= dateTo);
      if (customerFilter !== 'All') inv = inv.filter(t => t.partyId === customerFilter);
      if (employeeFilter !== 'All') inv = inv.filter(t => t.employeeId === employeeFilter);
      inv.sort((a, b) => (a.docDate || '').localeCompare(b.docDate || '') || (a.docNumber || '').localeCompare(b.docNumber || ''));

      // Receipts (CR/BR) indexed by the invoice they cleared
      const rIdx = {};
      all.filter(t => RECEIPT_CODES.includes(t.tranTypeCode) && t.status !== 'Cancelled').forEach(r => {
        const type = r.tranTypeCode === 'CR' ? 'Cash Receipt' : 'Bank Receipt';
        const account = r.cashAccountName || r.bankAccountName || r.paymentAccountName || '';
        (Array.isArray(r.invoiceAllocations) ? r.invoiceAllocations : []).forEach(a => {
          if (!a.invoiceId) return;
          (rIdx[a.invoiceId] = rIdx[a.invoiceId] || []).push({
            type, docNumber: r.docNumber, docDate: r.docDate, amount: round2(a.allocatedAmount), accountName: account,
            id: r.id, code: r.tranTypeCode,
          });
        });
      });

      // Returns (SR/NSR) indexed by parent invoice
      const retIdx = {};
      all.filter(t => RETURN_CODES.includes(t.tranTypeCode) && t.status !== 'Cancelled').forEach(r => {
        if (!r.parentId) return;
        (retIdx[r.parentId] = retIdx[r.parentId] || []).push(r);
      });

      // Source docs (QT/SO/DC) by id — for the Linked Documents grid
      const srcIdx = {};
      all.filter(t => SOURCE_CODES.includes(t.tranTypeCode)).forEach(s => { srcIdx[s.id] = s; });

      setInvoices(inv);
      setReceiptsByInvoice(rIdx);
      setReturnsByInvoice(retIdx);
      setSourceById(srcIdx);

      // Remember this generated view so Back restores it with 0 re-reads.
      saveMemory({
        selectedFYId, dateFrom, dateTo, customerFilter, employeeFilter,
        invoices: inv, receiptsByInvoice: rIdx, returnsByInvoice: retIdx, sourceById: srcIdx,
        hasGenerated: true,
      });
    } catch (e) {
      console.error('Error generating Sale Activity:', e);
    } finally {
      setLoading(false);
    }
  };

  // Master rows with computed Received / Returns / Balance
  const rows = useMemo(() => invoices.map(inv => {
    const rets = returnsByInvoice[inv.id] || [];
    const returnsTotal = round2(rets.reduce((s, r) => s + (Number(r.totalAmount) || 0), 0));
    const invoiceAmt = round2(inv.totalAmount);
    const received = round2(inv.paidAmount);
    const balance = round2(invoiceAmt - received - returnsTotal);
    return {
      id: inv.id, docNumber: inv.docNumber, docDate: inv.docDate,
      partyName: inv.partyName || 'Walk-in Customer',
      invoiceAmt, received, returnsTotal,
      wht: round2(inv.whtAmount), gst: round2(inv.taxAmount), discount: round2(inv.discountAmount),
      balance, status: inv.status,
      raw: inv,
    };
  }), [invoices, returnsByInvoice]);

  const totals = useMemo(() => rows.reduce((t, r) => ({
    invoiceAmt: t.invoiceAmt + r.invoiceAmt, received: t.received + r.received, returnsTotal: t.returnsTotal + r.returnsTotal,
    wht: t.wht + r.wht, gst: t.gst + r.gst, discount: t.discount + r.discount, balance: t.balance + r.balance,
  }), { invoiceAmt: 0, received: 0, returnsTotal: 0, wht: 0, gst: 0, discount: 0, balance: 0 }), [rows]);

  const selected = useMemo(() => rows.find(r => r.id === selectedId) || null, [rows, selectedId]);

  // Detail: receipts (inline payment + CR/BR), returns, linked source docs
  const detail = useMemo(() => {
    if (!selected) return null;
    const inv = selected.raw;
    const receipts = [];
    if (Number(inv.paymentAmount) > 0) {
      receipts.push({ type: 'Invoice Payment (inline)', docNumber: inv.docNumber, docDate: inv.docDate, amount: round2(inv.paymentAmount), accountName: inv.paymentAccountName || '', id: inv.id, code: inv.tranTypeCode });
    }
    (receiptsByInvoice[inv.id] || []).forEach(r => receipts.push(r));
    const receiptsTotal = round2(receipts.reduce((s, r) => s + r.amount, 0));

    const returns = (returnsByInvoice[inv.id] || []).map(r => ({
      type: r.tranTypeCode === 'NSR' ? 'Non-Tax Sale Return' : 'Sale Return',
      docNumber: r.docNumber, docDate: r.docDate, amount: round2(r.totalAmount),
      id: r.id, code: r.tranTypeCode,
    }));

    const parentIds = (Array.isArray(inv.parentIds) && inv.parentIds.length) ? inv.parentIds : (inv.parentId ? [inv.parentId] : []);
    const linked = [];
    parentIds.forEach(pid => {
      const s = sourceById[pid];
      if (s) {
        linked.push({ type: SOURCE_LABEL[s.tranTypeCode] || s.tranTypeCode, docNumber: s.docNumber, docDate: s.docDate, amount: round2(s.totalAmount), status: s.status, id: s.id, code: s.tranTypeCode });
        // Walk one level up: a Sale Order's own parent is the Quotation.
        if (s.tranTypeCode === 'SO' && s.parentId && sourceById[s.parentId]) {
          const qt = sourceById[s.parentId];
          linked.push({ type: SOURCE_LABEL[qt.tranTypeCode] || qt.tranTypeCode, docNumber: qt.docNumber, docDate: qt.docDate, amount: round2(qt.totalAmount), status: qt.status, id: qt.id, code: qt.tranTypeCode });
        }
      }
    });

    return { receipts, receiptsTotal, returns, linked };
  }, [selected, receiptsByInvoice, returnsByInvoice, sourceById]);

  const openDoc = (code, id) => {
    const routes = {
      SI: '/sales/sale-invoice', NSI: '/sales/non-tax-invoice',
      SR: '/sales/sale-return', NSR: '/sales/non-tax-sale-return',
      CR: '/receipts/cash-receipt', BR: '/receipts/bank-receipt',
      DC: '/sales/delivery-challan', SO: '/sales/sale-order', QT: '/sales/quotation',
    };
    if (routes[code] && id) navigate(routes[code], { state: { viewTransactionId: id } });
  };

  const handleExport = () => {
    if (rows.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Sale Activity Report - Invoice Wise\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${fy?.yearName || fy?.yearCode || ''}\n`;
    csv += `Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)}\n\n`;
    csv += 'Invoice No,Date,Customer,Invoice Amount,Received,Sale Returns,WHT,Sales Tax,Discount,Balance\n';
    rows.forEach(r => {
      csv += `${r.docNumber},${fmtDate(r.docDate)},"${(r.partyName || '').replace(/"/g, '""')}",${r.invoiceAmt},${r.received},${r.returnsTotal},${r.wht},${r.gst},${r.discount},${r.balance}\n`;
    });
    csv += `\nTotals,,,${totals.invoiceAmt},${totals.received},${totals.returnsTotal},${totals.wht},${totals.gst},${totals.discount},${totals.balance}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Sale_Activity_${dateFrom}_to_${dateTo}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (rows.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Sale Activity Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:26px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:4px}
        .meta{color:#666;font-size:11px;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;margin-bottom:14px}
        th{background:#800020;color:white;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
        th.amt{text-align:right}
        td{padding:5px 8px;border-bottom:1px solid #ddd;font-size:11px}
        td.amt{text-align:right;font-family:'Consolas',monospace}
        .tot{background:#800020;color:white;font-weight:700}
        .tot td{border-bottom:none}
        @media print{body{margin:12px}}
      </style></head><body>
      <h1>Sale Activity Report — Invoice Wise</h1>
      <div class="meta">${activeCompany?.companyName || 'Company'} | Financial Year: ${fy?.yearName || fy?.yearCode || ''} | Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)}</div>
      <table>
        <thead><tr><th>Invoice No</th><th>Date</th><th>Customer</th><th class="amt">Invoice Amt</th><th class="amt">Received</th><th class="amt">Returns</th><th class="amt">WHT</th><th class="amt">Sales Tax</th><th class="amt">Discount</th><th class="amt">Balance</th></tr></thead>
        <tbody>
          ${rows.map(r => `<tr><td>${r.docNumber}</td><td>${fmtDate(r.docDate)}</td><td>${r.partyName}</td>
            <td class="amt">${fmt(r.invoiceAmt)}</td><td class="amt">${fmt(r.received)}</td><td class="amt">${fmt(r.returnsTotal)}</td>
            <td class="amt">${fmt(r.wht)}</td><td class="amt">${fmt(r.gst)}</td><td class="amt">${fmt(r.discount)}</td><td class="amt">${fmt(r.balance)}</td></tr>`).join('')}
          <tr class="tot"><td colspan="3" style="text-align:right">Totals</td>
            <td class="amt">${fmt(totals.invoiceAmt)}</td><td class="amt">${fmt(totals.received)}</td><td class="amt">${fmt(totals.returnsTotal)}</td>
            <td class="amt">${fmt(totals.wht)}</td><td class="amt">${fmt(totals.gst)}</td><td class="amt">${fmt(totals.discount)}</td><td class="amt">${fmt(totals.balance)}</td></tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Sale Activity Report" breadcrumb="Reports / Sale Reports / Sale Activity">
      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FG>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}</option>)}
            </Select>
          </FG>
          <FG><Label>Date From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></FG>
          <FG><Label>Date To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></FG>
          <FG>
            <Label>Customer</Label>
            <CustomerSelect customers={customers} selectedCustomerId={customerFilter} onChange={setCustomerFilter} allOption={{ value: 'All', label: 'All Customers' }} />
          </FG>
          <FG>
            <Label>Salesperson</Label>
            <Select value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}>
              <option value="All">All Salesmen</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim()}</option>)}
            </Select>
          </FG>
          <BtnGroup>
            <Button onClick={handleExport} disabled={rows.length === 0}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={rows.length === 0}><FiPrinter /> Print</Button>
            <Button primary onClick={handleGenerate} disabled={loading || !selectedFYId}><FiBarChart2 /> {loading ? 'Loading...' : 'Display'}</Button>
          </BtnGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Master grid */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <Empty><FiBarChart2 size={40} /><p>Loading sale activity...</p></Empty>
          ) : !hasGenerated ? (
            <Empty><FiFileText size={40} /><p>Select a financial year and click Display to view invoice activity</p></Empty>
          ) : rows.length === 0 ? (
            <Empty><FiFileText size={40} /><p>No invoices found for the selected criteria</p></Empty>
          ) : (
            <Table>
              <thead>
                <Tr>
                  <Th>Invoice No</Th><Th>Date</Th><Th>Customer</Th>
                  <Th align="right">Invoice Amt</Th><Th align="right">Received</Th><Th align="right">Sale Returns</Th>
                  <Th align="right">WHT</Th><Th align="right">Sales Tax</Th><Th align="right">Discount</Th><Th align="right">Balance</Th>
                </Tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <Tr key={r.id} $selected={r.id === selectedId} onClick={() => setSelectedId(r.id)} style={{ cursor: 'pointer' }}>
                    <Td><DocLink onClick={(e) => { e.stopPropagation(); openDoc(r.raw.tranTypeCode, r.id); }}>{r.docNumber}</DocLink></Td>
                    <Td>{fmtDate(r.docDate)}</Td>
                    <Td>{r.partyName}</Td>
                    <Amt>{fmt(r.invoiceAmt)}</Amt>
                    <Amt>{r.received ? fmt(r.received) : '-'}</Amt>
                    <Amt>{r.returnsTotal ? fmt(r.returnsTotal) : '-'}</Amt>
                    <Amt>{r.wht ? fmt(r.wht) : '-'}</Amt>
                    <Amt>{r.gst ? fmt(r.gst) : '-'}</Amt>
                    <Amt>{r.discount ? fmt(r.discount) : '-'}</Amt>
                    <Amt $bold $pos={r.balance >= 0}>{fmt(r.balance)}</Amt>
                  </Tr>
                ))}
              </tbody>
              <tfoot>
                <TotalRow>
                  <Td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>Totals Rs.</Td>
                  <Amt $bold>{fmt(totals.invoiceAmt)}</Amt><Amt $bold>{fmt(totals.received)}</Amt><Amt $bold>{fmt(totals.returnsTotal)}</Amt>
                  <Amt $bold>{fmt(totals.wht)}</Amt><Amt $bold>{fmt(totals.gst)}</Amt><Amt $bold>{fmt(totals.discount)}</Amt><Amt $bold>{fmt(totals.balance)}</Amt>
                </TotalRow>
              </tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Detail grids for the selected invoice */}
      {selected && detail && (
        <DetailWrap>
          <DetailTitle><FiCornerDownRight /> Details for Invoice <b>{selected.docNumber}</b> — {selected.partyName}</DetailTitle>

          {/* Receipts */}
          <DetailCard>
            <DetailHead>Receipts / Receivings</DetailHead>
            <MiniTable>
              <thead><tr><MTh>Type</MTh><MTh>Voucher No</MTh><MTh>Date</MTh><MTh>Account</MTh><MTh align="right">Amount</MTh></tr></thead>
              <tbody>
                {detail.receipts.length === 0 ? (
                  <tr><MTd colSpan={5} style={{ color: colors.text.secondary }}>No receivings yet</MTd></tr>
                ) : detail.receipts.map((r, i) => (
                  <tr key={i}><MTd>{r.type}</MTd><MTd>{r.code ? <DocLink onClick={() => openDoc(r.code, r.id)}>{r.docNumber || '-'}</DocLink> : (r.docNumber || '-')}</MTd><MTd>{fmtDate(r.docDate)}</MTd><MTd>{r.accountName || '-'}</MTd><MTd align="right">{fmt(r.amount)}</MTd></tr>
                ))}
              </tbody>
              <tfoot>
                <tr><MTd colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Total Received</MTd><MTd align="right" style={{ fontWeight: 700 }}>{fmt(detail.receiptsTotal)}</MTd></tr>
                <tr><MTd colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Balance</MTd><MTd align="right" style={{ fontWeight: 700, color: selected.balance >= 0 ? '#1565C0' : '#C62828' }}>{fmt(selected.balance)}</MTd></tr>
              </tfoot>
            </MiniTable>
          </DetailCard>

          {/* Returns */}
          <DetailCard>
            <DetailHead>Sale Returns</DetailHead>
            <MiniTable>
              <thead><tr><MTh>Type</MTh><MTh>Doc No</MTh><MTh>Date</MTh><MTh align="right">Amount</MTh></tr></thead>
              <tbody>
                {detail.returns.length === 0 ? (
                  <tr><MTd colSpan={4} style={{ color: colors.text.secondary }}>No returns against this invoice</MTd></tr>
                ) : detail.returns.map((r, i) => (
                  <tr key={i}><MTd>{r.type}</MTd><MTd>{r.code ? <DocLink onClick={() => openDoc(r.code, r.id)}>{r.docNumber || '-'}</DocLink> : (r.docNumber || '-')}</MTd><MTd>{fmtDate(r.docDate)}</MTd><MTd align="right">{fmt(r.amount)}</MTd></tr>
                ))}
              </tbody>
              {detail.returns.length > 0 && (
                <tfoot><tr><MTd colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>Total Returns</MTd><MTd align="right" style={{ fontWeight: 700 }}>{fmt(selected.returnsTotal)}</MTd></tr></tfoot>
              )}
            </MiniTable>
          </DetailCard>

          {/* Linked documents */}
          <DetailCard>
            <DetailHead>Linked Documents (Quotation / Sale Order / Delivery Challan)</DetailHead>
            <MiniTable>
              <thead><tr><MTh>Type</MTh><MTh>Doc No</MTh><MTh>Date</MTh><MTh>Status</MTh><MTh align="right">Amount</MTh></tr></thead>
              <tbody>
                {detail.linked.length === 0 ? (
                  <tr><MTd colSpan={5} style={{ color: colors.text.secondary }}>Direct invoice — no linked source document</MTd></tr>
                ) : detail.linked.map((d, i) => (
                  <tr key={i}>
                    <MTd>{d.type}</MTd>
                    <MTd><DocLink onClick={() => openDoc(d.code, d.id)}>{d.docNumber || '-'}</DocLink></MTd>
                    <MTd>{fmtDate(d.docDate)}</MTd>
                    <MTd>{d.status || '-'}</MTd>
                    <MTd align="right">{fmt(d.amount)}</MTd>
                  </tr>
                ))}
              </tbody>
            </MiniTable>
          </DetailCard>
        </DetailWrap>
      )}
    </PageLayout>
  );
};

// ── Styled ──
const FiltersCard = styled.div`background:white;border-radius:${colors.borderRadius.lg};box-shadow:${colors.shadows.sm};padding:20px;margin-bottom:20px;`;
const FiltersRow = styled.div`display:flex;flex-wrap:wrap;gap:14px;align-items:flex-end;`;
const FG = styled.div`display:flex;flex-direction:column;gap:6px;min-width:150px;`;
const Label = styled.label`font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;letter-spacing:0.3px;`;
const Select = styled.select`padding:9px 12px;border:1px solid ${colors.neutral[300]};border-radius:${colors.borderRadius.md};font-size:13px;color:${colors.text.primary};background:white;cursor:pointer;&:focus{outline:none;border-color:${colors.primary.main};}`;
const Input = styled.input`padding:9px 12px;border:1px solid ${colors.neutral[300]};border-radius:${colors.borderRadius.md};font-size:13px;color:${colors.text.primary};&:focus{outline:none;border-color:${colors.primary.main};}`;
const BtnGroup = styled.div`display:flex;gap:8px;margin-left:auto;`;
const Button = styled.button`padding:9px 14px;border:1px solid ${colors.neutral[300]};border-radius:${colors.borderRadius.md};font-size:13px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:6px;white-space:nowrap;${p => p.primary ? `background:${colors.primary.main};color:white;border-color:${colors.primary.main};&:hover:not(:disabled){background:${colors.primary.dark};}` : `background:white;color:${colors.text.primary};&:hover:not(:disabled){background:${colors.neutral[100]};border-color:${colors.primary.main};}`}&:disabled{opacity:0.5;cursor:not-allowed;}`;
const ContentCard = styled.div`background:white;border-radius:${colors.borderRadius.lg};box-shadow:${colors.shadows.sm};overflow:hidden;margin-bottom:20px;`;
const TableContainer = styled.div`overflow-x:auto;`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`padding:11px 12px;text-align:${p => p.align || 'left'};font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;letter-spacing:0.4px;border-bottom:2px solid ${colors.neutral[200]};white-space:nowrap;background:${colors.neutral[50]};`;
const Tr = styled.tr`border-bottom:1px solid ${colors.neutral[100]};background:${p => p.$selected ? colors.primary.main + '12' : 'transparent'};&:hover{background:${p => p.$selected ? colors.primary.main + '18' : colors.neutral[50]};}`;
const Td = styled.td`padding:9px 12px;font-size:13px;color:${colors.text.primary};`;
const Amt = styled.td`padding:9px 12px;font-size:13px;text-align:right;font-family:'SF Mono','Consolas',monospace;white-space:nowrap;${p => p.$bold && 'font-weight:700;'}${p => p.$pos !== undefined && `color:${p.$pos ? '#1565C0' : '#C62828'};`}`;
const TotalRow = styled.tr`background:${colors.neutral[100]};border-top:2px solid ${colors.primary.main};td{padding:12px;}`;
const DocLink = styled.span`color:#1a73e8;cursor:pointer;font-weight:600;&:hover{text-decoration:underline;}`;
const Empty = styled.div`text-align:center;padding:56px 20px;color:${colors.text.secondary};svg{color:${colors.neutral[300]};margin-bottom:12px;}p{font-size:15px;}`;
const DetailWrap = styled.div`display:flex;flex-direction:column;gap:14px;`;
const DetailTitle = styled.div`display:flex;align-items:center;gap:8px;font-size:15px;color:${colors.text.primary};b{color:${colors.primary.main};}`;
const DetailCard = styled.div`background:white;border-radius:${colors.borderRadius.lg};box-shadow:${colors.shadows.sm};overflow:hidden;`;
const DetailHead = styled.div`padding:10px 14px;font-size:12px;font-weight:700;color:white;background:${colors.primary.main};text-transform:uppercase;letter-spacing:0.4px;`;
const MiniTable = styled.table`width:100%;border-collapse:collapse;`;
const MTh = styled.th`padding:8px 12px;text-align:${p => p.align || 'left'};font-size:10px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;border-bottom:1px solid ${colors.neutral[200]};background:${colors.neutral[50]};white-space:nowrap;`;
const MTd = styled.td`padding:7px 12px;text-align:${p => p.align || 'left'};font-size:12px;color:${colors.text.primary};border-bottom:1px solid ${colors.neutral[100]};${p => p.align === 'right' && `font-family:'SF Mono','Consolas',monospace;`}`;

export default SaleActivity;
